import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'
import * as Api from 'src/api'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import { useTheme } from 'src/state/providers/Theme'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import NoContent from 'src/views/components/NoContent'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

function userType(userType: string) {
  switch (userType) {
    case '1':
      return 'ADMINISTRATOR'
    case '2':
      return 'LECTURER'
    case '4':
      return 'STUDENT'
  }
}

export default function AdministratorRegimes() {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('admin_regimes:regimes')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent() {
  const headers = useAuthenticatedHeaders()
  const [searchParams] = useSearchParams({
    page: '1',
    perPage: '10',
  })
  const page = searchParams.get('page')!
  const perPage = searchParams.get('perPage')!
  const t = useTranslatable()
  const canManageRegimes = useAdminAccess().canManage('regimes')
  const { data: regimes, mutate: fetchRegimes } = useApi({
    endpoint: Api.getAdministratorRegimes,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          page,
          perPage,
        },
      }),
      [headers, page, perPage]
    ),
  })

  const activateRegime = async (id: string) => {
    try {
      await Api.getAdministratorRegimesActivate({
        headers,
        args: {
          id,
        },
      })
      await fetchRegimes()
      toast.success(t('admin_regimes:regime_successfully_activated'))
    } catch (error) {
      toast.error(t('error:an_error_occurred'))
    }
  }

  const locale = useLocale()
  const theme = useTheme()
  const breadcrumbsItems = [{ page: `${t('admin_regimes:regimes')}`, path: `/${locale}/administrator/regimes` }]

  return (
    <Section
      title={t('admin_regimes:regimes')}
      breadcrubms={breadcrumbsItems}
      icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faTools} />}
    >
      {regimes?.data != null && regimes.data.length === 0 ? (
        <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />
      ) : (
        <div className="w-full py-3">
          <Table.Table className="w-full">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ID</Table.Th>
                <Table.Th>{t('person:firstname')}</Table.Th>
                <Table.Th>{t('common:type')}</Table.Th>
                <Table.Th>{t('common:status')}</Table.Th>
                <Table.Th>{t('admin_regimes:allow_choose')}</Table.Th>
                <Table.Th>{t('admin_regimes:limit_functional')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {regimes.data.map((regime) => (
                <Table.Tr
                  key={regime.id}
                  className={
                    classNames('hover:bg-transparent', {
                      'bg-lightGreen': regime.status,
                    }) as string
                  }
                >
                  <Table.Td className="rounded-l-lg">{regime.id}</Table.Td>
                  <Table.Td>
                    <div className="flex items-center">
                      <Link
                        to={`/${locale}/administrator/sensitive/regimes/${regime.id}`}
                        className="mr-2 text-blue-500 hover:underline"
                      >
                        {regime.name}
                      </Link>
                      {!regime.status && canManageRegimes && (
                        <>
                          <Tooltip
                            id={`${regime.id}-activate-tooltip`}
                            place="top"
                            variant={theme === 'dark' ? 'dark' : 'light'}
                            className="mb-1 p-1"
                          >
                            {t('admin_regimes:activate')}
                          </Tooltip>

                          <button
                            data-tooltip-id={`${regime.id}-activate-tooltip`}
                            onClick={() => activateRegime(regime.id)}
                          >
                            <FontAwesomeIcon icon={ProRegularSvgIcons.faCircleRight} />
                          </button>
                        </>
                      )}
                    </div>
                  </Table.Td>
                  <Table.Td>{userType(regime.type)}</Table.Td>
                  <Table.Td>{regime.status ? t('common:enabled') : t('common:disabled')}</Table.Td>
                  <Table.Td>{regime.allowChoose ? t('common:yes') : t('common:no')}</Table.Td>
                  <Table.Td>{regime.limitFunctional ? t('common:yes') : t('common:no')}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table.Table>
        </div>
      )}
    </Section>
  )
}
